import * as assets from './rovo-third-party-apps-assets';
import * as smartlinkAssets from './rovo-smartlink-assets';

/**
 * Configuration for a 3rd party app integration.
 */
export type AppInfo = {
	/** URL to the app logo image */
	logo: string;
	/** Normalized name of the application */
	normalizedAppName: string;
	/** Following two types are used when the connector has a different naming convention for connector type
	 * Usually the convention is 'smartlinks-connector-<app-name>' for smartlink connectors and '<app-name>-connector' for full connectors
	 * But some connectors like Microsoft Teams and Google Drive have a different naming convention.
	 */
	smartlinkConnectorType?: string;
	fullConnectorType?: string;
	smartlinkLogo?: string;
};

export type AppId =
	| 'adobe-sign'
	| 'adobexd'
	| 'aha'
	| 'airtable'
	| 'amplitude'
	| 'asana'
	| 'azure-devops'
	| 'box'
	| 'clickup'
	| 'databricks'
	| 'docusign'
	| 'dovetail'
	| 'dropbox'
	| 'figma'
	| 'github'
	| 'gitlab'
	| 'gmail'
	| 'google-calendar'
	| 'drive'
	| 'hubspot'
	| 'launchdarkly'
	| 'lucid'
	| 'miro'
	| 'monday'
	| 'teams'
	| 'mural'
	| 'notion'
	| 'onedrive'
	| 'outlook-calendar'
	| 'outlook-mail'
	| 'pagerduty'
	| 'pipedrive'
	| 'power-bi'
	| 'salesforce'
	| 'sentry'
	| 'servicenow'
	| 'sharepoint'
	| 'slack'
	| 'smartsheet'
	| 'stripe'
	| 'todoist'
	| 'webex'
	| 'zendesk'
	| 'zeplin';

export const appIdToAppInfoMap: Record<AppId, AppInfo> = {
	'adobe-sign': {
		logo: assets.adobeLogo,
		normalizedAppName: 'Adobe Sign',
	},
	adobexd: {
		logo: assets.adobeXdLogo,
		smartlinkLogo: smartlinkAssets.adobeXdLogo,
		normalizedAppName: 'Adobe XD',
	},
	aha: {
		logo: assets.ahaLogo,
		normalizedAppName: 'Aha!',
	},
	airtable: {
		logo: assets.airtableLogo,
		smartlinkLogo: smartlinkAssets.airtableLogo,
		normalizedAppName: 'Airtable',
	},
	amplitude: {
		logo: assets.amplitudeLogo,
		normalizedAppName: 'Amplitude',
	},
	asana: {
		logo: assets.asanaLogo,
		smartlinkLogo: smartlinkAssets.asanaLogo,
		normalizedAppName: 'Asana',
	},
	'azure-devops': {
		logo: assets.azureLogo,
		smartlinkLogo: smartlinkAssets.azureDevopsLogo,
		normalizedAppName: 'Azure DevOps',
	},
	box: {
		logo: assets.boxLogo,
		smartlinkLogo: smartlinkAssets.boxLogo,
		normalizedAppName: 'Box',
	},
	clickup: {
		logo: assets.clickupLogo,
		smartlinkLogo: smartlinkAssets.clickupLogo,
		normalizedAppName: 'ClickUp',
	},
	databricks: {
		logo: assets.databricksLogo,
		normalizedAppName: 'Databricks',
	},
	docusign: {
		logo: assets.docusignLogo,
		smartlinkLogo: smartlinkAssets.docusignLogo,
		normalizedAppName: 'Docusign',
	},
	dovetail: {
		logo: assets.dovetailLogo,
		smartlinkLogo: smartlinkAssets.dovetailLogo,
		normalizedAppName: 'Dovetail',
	},
	dropbox: {
		logo: assets.dropboxLogo,
		smartlinkLogo: smartlinkAssets.dropboxLogo,
		normalizedAppName: 'Dropbox',
	},
	figma: {
		logo: assets.figmaLogo,
		smartlinkLogo: smartlinkAssets.figmaLogo,
		normalizedAppName: 'Figma',
	},
	github: {
		logo: assets.githubLogo,
		smartlinkLogo: smartlinkAssets.githubLogo,
		normalizedAppName: 'GitHub',
	},
	gitlab: {
		logo: assets.gitlabLogo,
		smartlinkLogo: smartlinkAssets.gitlabLogo,
		normalizedAppName: 'GitLab',
	},
	gmail: {
		logo: assets.gmailLogo,
		normalizedAppName: 'Gmail',
	},
	'google-calendar': {
		logo: assets.googleCalendarLogo,
		normalizedAppName: 'Google Calendar',
	},
	drive: {
		logo: assets.googleDriveLogo,
		smartlinkLogo: smartlinkAssets.googleDriveLogo,
		normalizedAppName: 'Google Drive',
		smartlinkConnectorType: 'smartlinks-connector-gdrive',
		fullConnectorType: 'google-drive-connector',
	},
	hubspot: {
		logo: assets.hubspotLogo,
		smartlinkLogo: smartlinkAssets.hubspotLogo,
		normalizedAppName: 'HubSpot',
	},
	launchdarkly: {
		logo: assets.launchdarklyLogo,
		smartlinkLogo: smartlinkAssets.launchdarklyLogo,
		normalizedAppName: 'LaunchDarkly',
	},
	lucid: {
		logo: assets.lucidLogo,
		smartlinkLogo: smartlinkAssets.lucidLogo,
		normalizedAppName: 'Lucid',
	},
	miro: {
		logo: assets.miroLogo,
		smartlinkLogo: smartlinkAssets.miroLogo,
		normalizedAppName: 'Miro',
	},
	monday: {
		logo: assets.mondayLogo,
		normalizedAppName: 'Monday.com',
	},
	teams: {
		logo: assets.msTeamsLogo,
		smartlinkLogo: smartlinkAssets.teamsLogo,
		normalizedAppName: 'Microsoft Teams',
		fullConnectorType: 'microsoft-teams-connector',
	},
	mural: {
		logo: assets.muralLogo,
		smartlinkLogo: smartlinkAssets.muralLogo,
		normalizedAppName: 'Mural',
	},
	notion: {
		logo: assets.notionLogo,
		smartlinkLogo: smartlinkAssets.notionLogo,
		normalizedAppName: 'Notion',
	},
	onedrive: {
		logo: assets.onedriveLogo,
		smartlinkLogo: smartlinkAssets.onedriveLogo,
		normalizedAppName: 'OneDrive',
	},
	'outlook-calendar': {
		logo: assets.outlookLogo,
		smartlinkLogo: smartlinkAssets.outlookLogo,
		normalizedAppName: 'Outlook Calendar',
	},
	'outlook-mail': {
		logo: assets.outlookLogo,
		smartlinkLogo: smartlinkAssets.outlookLogo,
		normalizedAppName: 'Outlook Mail',
	},
	pagerduty: {
		logo: assets.pagerdutyLogo,
		normalizedAppName: 'PagerDuty',
	},
	pipedrive: {
		logo: assets.pipedriveLogo,
		smartlinkLogo: smartlinkAssets.pipedriveLogo,
		normalizedAppName: 'Pipedrive',
	},
	'power-bi': {
		logo: assets.msPowerBiLogo,
		smartlinkLogo: smartlinkAssets.powerbiLogo,
		normalizedAppName: 'Power BI',
	},
	salesforce: {
		logo: assets.salesforceLogo,
		normalizedAppName: 'Salesforce',
	},
	sentry: {
		logo: assets.sentryLogo,
		normalizedAppName: 'Sentry',
	},
	servicenow: {
		logo: assets.servicenowLogo,
		normalizedAppName: 'ServiceNow',
	},
	sharepoint: {
		logo: assets.sharepointLogo,
		smartlinkLogo: smartlinkAssets.sharepointLogo,
		normalizedAppName: 'SharePoint',
	},
	slack: {
		logo: assets.slackLogo,
		smartlinkLogo: smartlinkAssets.slackLogo,
		normalizedAppName: 'Slack',
	},
	smartsheet: {
		logo: assets.smartsheetLogo,
		smartlinkLogo: smartlinkAssets.smartsheetLogo,
		normalizedAppName: 'Smartsheet',
	},
	stripe: {
		logo: assets.stripeLogo,
		smartlinkLogo: smartlinkAssets.stripeLogo,
		normalizedAppName: 'Stripe',
	},
	todoist: {
		logo: assets.todoistLogo,
		smartlinkLogo: smartlinkAssets.todoistLogo,
		normalizedAppName: 'Todoist',
	},
	webex: {
		logo: assets.webexLogo,
		smartlinkLogo: smartlinkAssets.webexLogo,
		normalizedAppName: 'Webex',
	},
	zendesk: {
		logo: assets.zendeskLogo,
		normalizedAppName: 'Zendesk',
	},
	zeplin: {
		logo: assets.zeplinLogo,
		smartlinkLogo: smartlinkAssets.zeplinLogo,
		normalizedAppName: 'Zeplin',
	},
} as const;

export const smartlinkExtensionKeyToAppIdMap: Record<string, AppId | AppId[]> = {
	// smart link provider mapping
	'adobe-xd-object-provider': 'adobexd',
	'amplitude-object-provider': 'amplitude',
	'airtable-object-provider': 'airtable',
	'asana-object-provider': 'asana',
	'azuredevops-object-provider': 'azure-devops',
	'box-object-provider': 'box',
	'clickup-object-provider': 'clickup',
	'docusign-object-provider': 'docusign',
	'dovetail-object-provider': 'dovetail',
	'dropbox-object-provider': 'dropbox',
	'figma-object-provider': 'figma',
	'github-object-provider': 'github',
	'gitlab-object-provider': 'gitlab',
	'google-object-provider': 'drive',
	'hubspot-object-provider': 'hubspot',
	'launchdarkly-object-provider': 'launchdarkly',
	'lucid-object-provider': 'lucid',
	'miro-object-provider': 'miro',
	'monday-object-provider': 'monday',
	'ms-power-bi-object-provider': 'power-bi',
	'ms-teams-object-provider': 'teams',
	'mural-object-provider': 'mural',
	'notion-object-provider': 'notion',
	'onedrive-object-provider': ['sharepoint', 'onedrive'], // sharepoint preferred when present in appList
	'pagerduty-object-provider': 'pagerduty',
	'pipedrive-object-provider': 'pipedrive',
	'salesforce-object-provider': 'salesforce',
	'sentry-object-provider': 'sentry',
	'slack-object-provider': 'slack',
	'smartsheet-object-provider': 'smartsheet',
	'stripe-object-provider': 'stripe',
	'todoist-object-provider': 'todoist',
	'webex-object-provider': 'webex',
	'zendesk-object-provider': 'zendesk',
	'zeplin-object-provider': 'zeplin',
};
